/**
 * ============================================================
 * KEJJA OS — src/lib/financeApi.ts  (v1.1)
 * Typed API for Finance: overview + pulse + bills + funds + credit.
 * ============================================================
 */

import { api } from '@/lib/api';

// ------------------------------------------------------------
// Shared types (v1 core)
// ------------------------------------------------------------

export type FinanceAccountType = 'cash' | 'bank' | 'mpesa' | 'sacco' | 'credit_card' | 'other';
export type FinanceCategoryKind = 'income' | 'expense' | 'transfer';
export type FinanceDirection = 'in' | 'out';
export type FinanceSource = 'manual' | 'mpesa_sms' | 'mpesa_api' | 'bank_import' | 'ai_suggestion';

export type BillCadence = 'weekly' | 'monthly' | 'termly' | 'annual' | 'one_off';
export type CreditKind = 'fuliza' | 'mshwari' | 'okoa_jahazi' | 'kcb_mpesa' | 'tala' | 'branch' | 'sacco_loan' | 'bank_overdraft' | 'other';

export interface FinanceAccount {
  id: string; homeId: string; name: string;
  accountType: FinanceAccountType;
  currencyCode: string;
  openingBalance: number;
  institution: string | null;
  accountNumberMasked: string | null;
  color: string | null;
  icon: string | null;
  isActive: boolean;
  createdAt: string; updatedAt: string; deletedAt: string | null;
}

export interface FinanceCategory {
  id: string; homeId: string; name: string;
  kind: FinanceCategoryKind;
  color: string | null; icon: string | null;
  keywords: string[]; sortOrder: number;
  isActive: boolean;
  createdAt: string; updatedAt: string; deletedAt: string | null;
}

export interface FinanceTransaction {
  id: string; homeId: string;
  accountId: string | null; categoryId: string | null;
  createdByUserId: string | null;
  direction: FinanceDirection;
  amount: number; currencyCode: string;
  merchant: string | null; description: string | null; reference: string | null;
  occurredAt: string;
  source: FinanceSource; sourceRaw: string | null;
  aiCategorized: boolean; aiConfidence: number | null; aiCallLogId: string | null;
  tagsJson: string[];
  createdAt: string; updatedAt: string; deletedAt: string | null;
  // v1.1
  usedCredit?: boolean;
  creditLineId?: string | null;
  creditAmount?: number;
  runningBalance?: number | null;
}

export interface FinanceBudget {
  id: string; homeId: string; categoryId: string;
  amount: number; currencyCode: string;
  periodYear: number; periodMonth: number;
  note: string | null;
}

export interface FinanceOverview {
  year: number; month: number; currencyCode: string;
  totalIn: number; totalOut: number; netFlow: number;
  transactionCount: number;
  byCategory: Array<{
    categoryId: string | null; categoryName: string | null;
    categoryColor: string | null; categoryIcon: string | null;
    kind: 'income' | 'expense' | 'transfer' | 'uncategorized';
    totalOut: number; totalIn: number; txCount: number;
    budgetAmount: number | null; budgetUsedPct: number | null;
  }>;
  recentTransactions: FinanceTransaction[];
}

// ------------------------------------------------------------
// v1.1 types
// ------------------------------------------------------------

export interface FinanceBill {
  id: string; homeId: string;
  categoryId: string | null;
  name: string; amount: number; currencyCode: string;
  cadence: BillCadence;
  nextDueDate: string;  // YYYY-MM-DD
  isHardBill: boolean; autoPay: boolean;
  paidThroughDate: string | null;
  lastPaidAt: string | null;
  note: string | null;
  icon: string | null; color: string | null;
  isActive: boolean;
  createdAt: string; updatedAt: string; deletedAt: string | null;
}

export interface FinanceSinkingFund {
  id: string; homeId: string;
  name: string;
  targetAmount: number; savedAmount: number; currencyCode: string;
  targetDate: string | null;
  priority: number;
  icon: string | null; color: string | null;
  note: string | null;
  isAchieved: boolean;
  createdAt: string; updatedAt: string; deletedAt: string | null;
}

export interface FinanceCreditLine {
  id: string; homeId: string;
  kind: CreditKind;
  name: string;
  limitAmount: number; usedAmount: number; currencyCode: string;
  lastUsedAt: string | null;
  note: string | null;
  color: string | null;
  isActive: boolean;
  createdAt: string; updatedAt: string; deletedAt: string | null;
}

export interface Pulse {
  currencyCode: string;
  generatedAt: string;
  liquidity: {
    total: number;
    byAccount: Array<{
      accountId: string; name: string; accountType: string;
      balance: number; color: string | null; icon: string | null;
    }>;
  };
  upcomingBills: {
    total: number; withinDays: number;
    items: Array<{
      id: string; name: string; amount: number;
      nextDueDate: string; daysUntil: number;
      isHardBill: boolean;
      icon: string | null; color: string | null;
    }>;
  };
  safeToSpend: number;
  credit: {
    totalUsed: number; totalLimit: number;
    lines: Array<{
      id: string; kind: string; name: string;
      usedAmount: number; limitAmount: number; percentUsed: number;
      color: string | null;
    }>;
  };
  funds: Array<{
    id: string; name: string;
    targetAmount: number; savedAmount: number; percent: number;
    targetDate: string | null; daysUntilTarget: number | null;
    color: string | null; icon: string | null;
  }>;
  budget: {
    year: number; month: number;
    totalBudget: number; totalSpent: number;
    percentUsed: number;
    categoryCount: number; overCategoryCount: number;
  };
  netWorth: {
    current: number;
    cash: number; savings: number; debt: number;
    lastMonth: number;
    delta: number; deltaPercent: number;
  };
  cashflow: {
    in: number; out: number; toSavings: number; net: number;
  };
  savingsRate: number;
  spendByCategory: Array<{
    categoryId: string | null;
    name: string;
    color: string | null;
    amount: number;
    percent: number;
  }>;
  last30DaysSpend: Array<{
    date: string;
    amount: number;
  }>;
  week: {
    in: number; out: number; net: number; txCount: number;
    avgWeeklyOut: number; deltaVsAvg: number;
  };
  recent: FinanceTransaction[];
}

export interface MpesaPreview {
  parsed: boolean;
  preview?: {
    direction: FinanceDirection;
    amount: number;
    currencyCode: string;
    merchant: string | null;
    reference: string | null;
    occurredAt: string | null;
    description: string;
    usedCredit: boolean;
    creditAmount: number;
    creditKind: 'fuliza' | 'mshwari' | 'okoa_jahazi' | null;
    runningBalance: number | null;
    transactionCost: number;
  };
  error?: string;
}

// ------------------------------------------------------------
// API
// ------------------------------------------------------------

const base = (homeId: string) => `/homes/${homeId}/finance`;

export const financeApi = {
  // Pulse
  pulse: (homeId: string, currencyCode = 'KES') =>
    api.get<Pulse>(`${base(homeId)}/pulse?currencyCode=${currencyCode}`),

  // Overview (legacy monthly)
  overview: (homeId: string, year?: number, month?: number) => {
    const qs = new URLSearchParams();
    if (year) qs.set('year', String(year));
    if (month) qs.set('month', String(month));
    const suffix = qs.toString() ? `?${qs}` : '';
    return api.get<FinanceOverview>(`${base(homeId)}/overview${suffix}`);
  },

  seed: (homeId: string) => api.post<{ seeded: boolean }>(`${base(homeId)}/seed`),

  // Accounts
  listAccounts: (homeId: string) => api.get<{ accounts: FinanceAccount[] }>(`${base(homeId)}/accounts`),
  createAccount: (homeId: string, body: {
    name: string; accountType: FinanceAccountType; currencyCode?: string;
    openingBalance?: number; institution?: string | null;
    color?: string | null; icon?: string | null;
  }) => api.post<{ account: FinanceAccount }>(`${base(homeId)}/accounts`, body),
  updateAccount: (homeId: string, accountId: string, body: Partial<{
    name: string; accountType: FinanceAccountType; openingBalance: number;
  }>) => api.patch<{ account: FinanceAccount }>(`${base(homeId)}/accounts/${accountId}`, body),
  deleteAccount: (homeId: string, accountId: string) =>
    api.delete<void>(`${base(homeId)}/accounts/${accountId}`),

  // Categories
  listCategories: (homeId: string) =>
    api.get<{ categories: FinanceCategory[] }>(`${base(homeId)}/categories`),
  createCategory: (homeId: string, body: {
    name: string; kind: FinanceCategoryKind; color?: string | null;
    icon?: string | null; keywords?: string[];
  }) => api.post<{ category: FinanceCategory }>(`${base(homeId)}/categories`, body),
  updateCategory: (homeId: string, catId: string, body: Partial<{
    name: string; kind: FinanceCategoryKind; keywords: string[];
  }>) => api.patch<{ category: FinanceCategory }>(`${base(homeId)}/categories/${catId}`, body),
  deleteCategory: (homeId: string, catId: string) =>
    api.delete<void>(`${base(homeId)}/categories/${catId}`),

  // Transactions
  listTransactions: (homeId: string, filter: {
    limit?: number; offset?: number; fromDate?: string; toDate?: string;
    categoryId?: string; accountId?: string; direction?: FinanceDirection;
  } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => { if (v !== undefined) qs.set(k, String(v)); });
    const suffix = qs.toString() ? `?${qs}` : '';
    return api.get<{ transactions: FinanceTransaction[] }>(`${base(homeId)}/transactions${suffix}`);
  },
  createTransaction: (homeId: string, body: {
    accountId?: string | null; categoryId?: string | null;
    direction: FinanceDirection; amount: number; currencyCode?: string;
    merchant?: string | null; description?: string | null;
    reference?: string | null; occurredAt?: string; tagsJson?: string[];
  }) => api.post<{ transaction: FinanceTransaction }>(`${base(homeId)}/transactions`, body),
  updateTransaction: (homeId: string, txId: string, body: Partial<{
    categoryId: string | null; direction: FinanceDirection; amount: number;
    merchant: string | null; description: string | null; occurredAt: string;
  }>) => api.patch<{ transaction: FinanceTransaction }>(`${base(homeId)}/transactions/${txId}`, body),
  deleteTransaction: (homeId: string, txId: string) =>
    api.delete<void>(`${base(homeId)}/transactions/${txId}`),

  // M-Pesa
  mpesaPreview: (homeId: string, rawSms: string) =>
    api.post<MpesaPreview>(`${base(homeId)}/mpesa/preview`, { rawSms }),
  mpesaImport: (homeId: string, rawSms: string, mpesaAccountId?: string | null) =>
    api.post<{ parsed: boolean; transaction?: FinanceTransaction; error?: string; creditBumped?: { lineId: string; lineName: string; delta: number } }>(
      `${base(homeId)}/mpesa/import`,
      { rawSms, mpesaAccountId: mpesaAccountId ?? null },
    ),

  // Budgets
  listBudgets: (homeId: string, year: number, month: number) =>
    api.get<{ budgets: FinanceBudget[] }>(`${base(homeId)}/budgets?year=${year}&month=${month}`),
  upsertBudget: (homeId: string, body: {
    categoryId: string; amount: number; currencyCode?: string;
    periodYear: number; periodMonth: number; note?: string | null;
  }) => api.patch<{ budget: FinanceBudget }>(`${base(homeId)}/budgets`, body),
  deleteBudget: (homeId: string, budgetId: string) =>
    api.delete<void>(`${base(homeId)}/budgets/${budgetId}`),

  // Bills
  listBills: (homeId: string) => api.get<{ bills: FinanceBill[] }>(`${base(homeId)}/bills`),
  createBill: (homeId: string, body: {
    name: string; amount: number; currencyCode?: string;
    cadence: BillCadence; nextDueDate: string;
    categoryId?: string | null; isHardBill?: boolean; autoPay?: boolean;
    note?: string | null; icon?: string | null; color?: string | null;
  }) => api.post<{ bill: FinanceBill }>(`${base(homeId)}/bills`, body),
  updateBill: (homeId: string, billId: string, body: Partial<{
    name: string; amount: number; cadence: BillCadence; nextDueDate: string;
    isHardBill: boolean; note: string | null;
  }>) => api.patch<{ bill: FinanceBill }>(`${base(homeId)}/bills/${billId}`, body),
  markBillPaid: (homeId: string, billId: string) =>
    api.post<{ bill: FinanceBill }>(`${base(homeId)}/bills/${billId}/mark-paid`),
  deleteBill: (homeId: string, billId: string) =>
    api.delete<void>(`${base(homeId)}/bills/${billId}`),

  // Funds
  listFunds: (homeId: string) => api.get<{ funds: FinanceSinkingFund[] }>(`${base(homeId)}/funds`),
  createFund: (homeId: string, body: {
    name: string; targetAmount: number; savedAmount?: number;
    currencyCode?: string; targetDate?: string | null; priority?: number;
    icon?: string | null; color?: string | null; note?: string | null;
  }) => api.post<{ fund: FinanceSinkingFund }>(`${base(homeId)}/funds`, body),
  updateFund: (homeId: string, fundId: string, body: Partial<{
    name: string; targetAmount: number; targetDate: string | null; priority: number;
  }>) => api.patch<{ fund: FinanceSinkingFund }>(`${base(homeId)}/funds/${fundId}`, body),
  contributeToFund: (homeId: string, fundId: string, delta: number) =>
    api.post<{ fund: FinanceSinkingFund }>(`${base(homeId)}/funds/${fundId}/contribute`, { delta }),
  deleteFund: (homeId: string, fundId: string) =>
    api.delete<void>(`${base(homeId)}/funds/${fundId}`),

  // Credit lines
  listCreditLines: (homeId: string) =>
    api.get<{ creditLines: FinanceCreditLine[] }>(`${base(homeId)}/credit-lines`),
  createCreditLine: (homeId: string, body: {
    kind: CreditKind; name: string;
    limitAmount?: number; usedAmount?: number;
    currencyCode?: string; note?: string | null; color?: string | null;
  }) => api.post<{ creditLine: FinanceCreditLine }>(`${base(homeId)}/credit-lines`, body),
  updateCreditLine: (homeId: string, creditId: string, body: Partial<{
    name: string; limitAmount: number; usedAmount: number;
  }>) => api.patch<{ creditLine: FinanceCreditLine }>(`${base(homeId)}/credit-lines/${creditId}`, body),
  deleteCreditLine: (homeId: string, creditId: string) =>
    api.delete<void>(`${base(homeId)}/credit-lines/${creditId}`),
};
